/**
 * @module app.cm
 */

import { autoinject, bindingMode, TaskQueue } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CaseService, ICaseSearchFilters } from 'app/cm/services/case-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IMemberSearchFilters } from 'app/cd/services/member-service';
import { SelectEvent, EssTableCustomElement } from 'app/shared/elements/ess-table';
import { CaseWithLocalState } from 'app/cm/models/local-state-extensions';
import { AssignCasesPageContext } from 'app/cm/lib/assign-cases-context';
import { fireEvent } from 'app/shared/lib/fire-events';
import { guid } from 'app/shared/lib/guid';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AssignCasesClearBulkActionEvent } from 'app/cm/lib/events';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions';
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import { requiredFields as memberRequiredFields } from 'app/cd/elements/members/member-display';
import * as CM from 'app/cm/models/essette-api';


interface ICaseSelectCustomEvent { ownerId: string, cases: ReadonlyArray<CM.Case>, casesRemoved: Array<CM.Case> }
export type CaseSelectCustomEvent = CustomEvent<ICaseSelectCustomEvent>;

interface IOpenCaseQueuesCustomEvent { case: CM.Case }
export type OpenCaseQueuesCustomEvent = CustomEvent<IOpenCaseQueuesCustomEvent>;

@autoinject()
export class CaseOwnerCasesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public ownerId: string | null = null;

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public ownerDisplay: string | null = null;

    @bindable.arrayNull({ defaultBindingMode: bindingMode.toView })
    public cases: ReadonlyArray<CaseWithLocalState> | null = null;

    protected showCaseSummaryLink: boolean = false;
    protected hasCaseRoute = false;
    protected openCaseSummary = openCaseSummary;
    private assignCasesClearBulkActionEvent: Subscription | null = null;
    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    @bindable.number({ defaultBindingMode: bindingMode.toView })
    protected pageSize: number = 20;
    protected id: guid = guid();


    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<CaseWithLocalState>;
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly context: AssignCasesPageContext,
        private readonly queue: TaskQueue,
        private readonly service: CaseService,
        private readonly events: EventAggregator,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {

        super(...rest);

        // Default sort
        this.sorting.entries = [
            {
                reference: 'metadata.rollupModified.timestamp',
                direction: SortDirection.Ascending
            },
            {
                reference: 'number',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        this.loadCustomFieldTargets();
        this.reload();

        this.assignCasesClearBulkActionEvent = this.events.subscribe
            (AssignCasesClearBulkActionEvent, (e: AssignCasesClearBulkActionEvent) => {
                // Deselect table
                if(this.$table) {
                    this.$table.deselectAll();
                    // Deselect other rows selected from other pages
                    this.$table.selected.splice(0);
                }
            });
    }

    protected attached(): void { }

    protected detached(): void {
        if(this.assignCasesClearBulkActionEvent) {
            this.assignCasesClearBulkActionEvent.dispose();
        }

        if(this.ownerId) { this.context.clearSelectedCases(this.ownerId); }
        this.$table.selected.splice(0);
    }

    ////****************** Events ***************************************************////
    protected casesSelected(event: SelectEvent<CM.Case>): void {
        if(!this.ownerId && this.ownerId !== '') return;
        const detail: ICaseSelectCustomEvent = { ownerId: this.ownerId, cases: event.detail.items, casesRemoved: [] };
        // if no item and no items selected, a "deselect all" action has been taken, so pass along which cases need to be removed
        if(!event.detail.item && event.detail.items.length === 0 && this.cases) {
            detail.casesRemoved = [...this.cases];
        } else {
            // otherwise, check if a singular case has been deselected and pass that along by itself
            const caseRemovedIndex = event.detail.items.findIndex(item => item.id === event.detail.item?.id);
            if(event.detail.item && caseRemovedIndex === -1) detail.casesRemoved.push(event.detail.item);
        }

        fireEvent(this.$element, 'case-select', detail);
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
    }

    public async reload(): Promise<void> {
        this.checkPermissions();

        if(this.ownerId || this.ownerId === '') {
            this.loadOwnerCases(this.ownerId);
        }
    }

    protected openQueueHistory($case: CM.Case): void {
        if(this.$element && !!$case && !!$case.id) {
            const detail: IOpenCaseQueuesCustomEvent = { case: $case };
            fireEvent(this.$element, 'open-queue', detail);
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadOwnerCases(ownerId: string | null): Promise<void> {
        const caseFilters: ICaseSearchFilters = { assignedTo: { id: ownerId }, openOnly: true };
        const memberFilters: IMemberSearchFilters = {};

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes: string[] = [
            'program',
            'acuity',
            'intensity',
            'status',
            'member',
            'member.primaryPlan.healthPlan',
            'member.primaryPlan.lineOfBusiness',
            'queues'
        ];
        const fields: string[] = [
            'acuity.description',
            'custom.lookup.*',
            'custom.text.*',
            'intensity.description',
            'isCheckedOut',
            'program.description',
            'status.description',
            'member.eligibility.isCurrent',
            'member.primaryPlan.healthPlan.description',
            'member.primaryPlan.lineOfBusiness.description',
            'metadata.created.timestamp',
            'metadata.rollupModified.timestamp',
            'number',
            'queues.description'
        ];

        memberRequiredFields.forEach(field => !fields.includes(`member.${field}`) ? fields.push(`member.${field}`) : null);


        try {
            this.state.beginLoad();
            const response = await this.service.searchCases(caseFilters, memberFilters, options, includes, fields);
            this.cases = response.results;

            // this is needed for member-filter assignment validation
            fireEvent(this.$element, 'cases-loaded', { ownerId: this.ownerId, cases: this.cases });
            // Give aurelia a chance to bind data to the table before we ask the table to restore selected items
            this.queue.queueTask(() => this.$table.restoreSelected(this.context.getSelectedCases(this.ownerId || '')));

            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CM.Case');
    }
}
